/**
 * @file `useChallengeStore` — Pinia store for the admin Challenge library. Loads every
 * challenge once into memory, then drives the search / filter / paginate UX entirely
 * client-side. Persisted across reloads so the admin doesn't pay the full collection cost
 * every navigation.
 *
 * Filter functions are returned as predicates (rather than mutating state) so the caller
 * can compose them — pass any combination to `applyFilters` and the final list lands in
 * `filteredChallenges`. The `fetchChallengeBatch` action then slices that list for UI rendering.
 *
 * @example
 * const store = useChallengeStore()
 * await store.fetchAllChallenges()
 * store.applyFilters([store.searchChallenges('crimp'), store.filterChallengesByCategory('Physical')])
 * const page = store.fetchChallengeBatch(0, 9)
 */

import { defineStore } from 'pinia'
import type { Challenge } from '@/types/Types'
import { getAllChallenges } from '@/stores/challengeService'
import { standardizeString } from '@/stores/methods'

export const useChallengeStore = defineStore('challenge', {
  state: (): ChallengeState => ({
    challenges: [],
    filteredChallenges: []
  }),
  persist: true,

  getters: {},
  actions: {
    /** Loads the full challenges collection into memory and resets the filtered view to "everything". Used by the admin challenge library and by the player-facing blacklist modal (to resolve blacklisted ids to localized titles and compute the blacklist cap). */
    fetchAllChallenges: async (state: ChallengeState) => {
      state.challenges = await getAllChallenges('', false)
      state.filteredChallenges = state.challenges
    },
    /** Returns a predicate that matches a challenge on accent-insensitive substring against both languages of title and description. Empty search returns a tautology. */
    searchChallenges: (search: string) => {
      if (search === '') {
        return () => true
      }

      const cleanedSearch = standardizeString(search)

      return (challenge: Challenge) => {
        return (
          standardizeString(challenge.title).includes(cleanedSearch) ||
          standardizeString(challenge.titleEnglish).includes(cleanedSearch) ||
          standardizeString(challenge.description).includes(cleanedSearch) ||
          standardizeString(challenge.descriptionEnglish).includes(cleanedSearch)
        )
      }
    },
    /** Returns a predicate that keeps challenges whose `packs` array includes `packId`. */
    filterChallengesByPack: (packId: string) => {
      return (challenge: Challenge) => challenge.packs?.includes(packId)
    },
    /** Returns a predicate that keeps challenges matching the given category exactly. */
    filterChallengesByCategory: (category: string) => {
      return (challenge: Challenge) => challenge.category === category
    },
    /** Applies every predicate as AND (`every`) and stores the survivors in `filteredChallenges`. Predicates may come from `searchChallenges`, `filterChallengesByPack`, etc. */
    applyFilters: (
      state: ChallengeState,
      filters: ((challenge: Challenge) => boolean | undefined)[]
    ) => {
      state.filteredChallenges = state.challenges.filter((challenge) =>
        filters.every((filter) => filter(challenge))
      )
    },
    /** Returns a slice of the current filtered view starting at `startingPoint`. Default page size is 9 (matches `getAllChallenges` server-side limit for consistency). */
    fetchChallengeBatch: (state: ChallengeState, startingPoint: number = 0, batch: number = 9) => {
      return state.filteredChallenges.slice(startingPoint, startingPoint + batch)
    }
  }
})

interface ChallengeState {
  challenges: Challenge[] | []
  filteredChallenges: Challenge[] | []
}
